/**
 * The design tokens of `DESIGN.md` §3, in one place.
 *
 * `tailwind.config.ts` builds the theme from this module and the tests measure it from the same
 * module, so there is exactly one copy of every value. Two hand-kept copies is the drift this
 * project keeps having to fix.
 *
 * What is deliberately absent: a second radius, a second shadow, a dark palette, and any semantic
 * colour beyond the two below. All three are decisions, not omissions — see §3 and
 * "What is deliberately not designed here".
 */

/**
 * Ink and paper are warm neutrals and carry the entire interface. Everything else appears only
 * where it means something.
 */
export const colors = {
  ink: {
    900: '#16150F',
    700: '#45413B',
    500: '#726D65',
    /** Never text — 2.4:1 on paper-bg. Status dots, disabled icon strokes, decorative marks. */
    300: '#A8A29A',
  },
  paper: {
    DEFAULT: '#FFFFFF',
    bg: '#FAF9F7',
    sunken: '#F2F0EC',
  },
  rule: {
    /** Decorative hairlines only: dividers, table rows, section rules. */
    DEFAULT: '#E5E2DC',
    strong: '#D0CCC4',
    /** Borders of interactive controls — WCAG 1.4.11 wants 3:1 and `rule` reaches 1.2:1. */
    control: '#948E84',
  },
  positive: {
    DEFAULT: '#15803D',
    wash: '#EEF7F1',
  },
  negative: {
    DEFAULT: '#BE123C',
    wash: '#FDF0F3',
  },
} as const;

/**
 * The white-label values (§2). A tenant may change the accent, its foreground and the logo — and
 * nothing else. Semantic colours carry meaning and must not bend to a brand palette.
 *
 * Held as hex here and emitted as bare HSL channels in `index.css` (`hsl(var(--accent) / 0.08)`
 * has to compose), with `hexToHslChannels` as the one conversion and a test asserting the two
 * agree. §2 wrote the accent both ways — `--accent: 21 89% 41%` beside the comment `#C2410C` —
 * and they are **not the same colour**: those channels render `#C64D0C`, which measures 4.5:1 on
 * paper where §9 states 4.9:1, and 4.7:1 for white on the button where §9 states 5.2:1. The hex
 * is what §9 measured and what the design means, so the hex is the source of truth here and
 * `DESIGN.md` §2 was corrected to match rather than left with two answers.
 */
export const whiteLabel = {
  accent: '#C2410C',
  accentForeground: '#FFFFFF',
} as const;

/**
 * Radius is 4px for every rectangle. `rounded-full` is a shape, not a second radius, and there is
 * no 2px and no 3px — "they are the decision taken three times".
 */
export const radius = '4px';

/**
 * There are no shadows. Separation is `border border-rule`. The single exception is content that
 * floats above the page: dialogs, dropdowns, popovers, toasts.
 */
export const overlayShadow = '0 8px 32px rgb(22 21 15 / 0.12)';

/** 120ms ease-out, and only on these four properties (§3 Motion). */
export const motion = {
  duration: '120ms',
  easing: 'cubic-bezier(0, 0, 0.2, 1)', // ease-out
  properties: ['background-color', 'border-color', 'opacity', 'transform'] as const,
};
